/** Магазин.
 *
 * Витрина показывает не название оформления, а само оформление: рядом
 * с каждым — настоящая доска в его цветах, собранная тем же компонентом,
 * что и боевая. Образец, нарисованный отдельно, рано или поздно разойдётся
 * с тем, что игрок получит после покупки.
 *
 * Одно оформление не продаётся, а открывается серией дней. Без него
 * витрина остаётся только кассой, а у игрока без покупок в ней нет цели.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../api/client'
import { buyItem, fetchShop, selectTheme } from '../api/endpoints'
import type { ShopItem, ShopState } from '../api/types'
import { Board } from '../components/Board'
import { Dialog } from '../components/Sheet'
import { BalanceChip, Crystal, ProgressBar } from '../components/ui'
import { t } from '../i18n'
import { applyBoardTheme, backButton } from '../telegram/webapp'

/** Кусок настоящей задачи для образца: пустая доска ничего не показывает. */
const SAMPLE_GIVENS =
  '53..7....6..195....98....6.8...6...34..8.3..17...2...6.6....28....419..5....8..79'
const SAMPLE_BOARD =
  '534.7....6..195....98....6.8...6...34..8.3..17...2...6.6....28....419..5....8..79'
const SAMPLE_NOTES = { '3': [2, 4, 6] }

export interface ShopScreenProps {
  onExit: () => void
  onThemeChange: (code: string) => void
  onBalanceChange: (balance: number) => void
  onOpenCrystals: () => void
}

export function ShopScreen({
  onExit,
  onThemeChange,
  onBalanceChange,
  onOpenCrystals,
}: ShopScreenProps) {
  const [state, setState] = useState<ShopState | null>(null)
  const [busy, setBusy] = useState(false)
  const [shortfall, setShortfall] = useState<number | null>(null)

  useEffect(() => backButton.show(onExit), [onExit])

  const load = useCallback(async () => {
    const shop = await fetchShop()
    setState(shop)
    onBalanceChange(shop.balance)
  }, [onBalanceChange])

  useEffect(() => {
    void load().catch(() => setState(null))
  }, [load])

  const buy = useCallback(
    async (code: string) => {
      setBusy(true)
      try {
        const next = await buyItem(code, `shop:${code}`)
        setState(next)
        onBalanceChange(next.balance)
      } catch (failure) {
        if (failure instanceof ApiError && failure.code === 'insufficient_funds') {
          setShortfall((failure.body.required ?? 0) - (failure.body.balance ?? 0))
        }
      } finally {
        setBusy(false)
      }
    },
    [onBalanceChange],
  )

  const equip = useCallback(
    async (code: string) => {
      setBusy(true)
      try {
        const next = await selectTheme(code)
        setState(next)
        // Оформление применяется сразу: покупка, которую не видно, не
        // ощущается покупкой.
        applyBoardTheme(next.theme)
        onThemeChange(next.theme)
      } finally {
        setBusy(false)
      }
    },
    [onThemeChange],
  )

  if (!state) {
    return (
      <div className="screen screen--center">
        <span className="hint-text">{t('app.loading')}</span>
      </div>
    )
  }

  const themes = state.items.filter((item) => item.kind === 'theme')
  const frames = state.items.filter((item) => item.kind === 'frame')

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('shop.title')}</span>
        <BalanceChip balance={state.balance} />
      </header>

      <div className="screen__body">
        <span className="hint-text">{t('shop.note')}</span>

        {themes.map((item) => (
          <ThemeCard
            key={item.code}
            item={item}
            balance={state.balance}
            busy={busy}
            onBuy={() => void buy(item.code)}
            onEquip={() => void equip(item.code)}
          />
        ))}

        {frames.length > 0 && (
          <>
            <span className="section-label" style={{ marginTop: 'var(--gap-3)' }}>
              {t('shop.frames')}
            </span>
            <span className="hint-text">{t('shop.framesNote')}</span>
            {frames.map((item) => (
              <FrameRow
                key={item.code}
                item={item}
                balance={state.balance}
                busy={busy}
                onBuy={() => void buy(item.code)}
                onEquip={() => void equip(item.code)}
              />
            ))}
          </>
        )}
      </div>

      {shortfall !== null && (
        <Dialog title={t('dialog.funds.title')} onDismiss={() => setShortfall(null)}>
          <p className="hint-text" style={{ margin: 0 }}>
            {t('shop.notEnough', { amount: shortfall })}
          </p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              setShortfall(null)
              onOpenCrystals()
            }}
          >
            {t('dialog.funds.earn')}
          </button>
          <button type="button" className="button" onClick={() => setShortfall(null)}>
            {t('dialog.funds.later')}
          </button>
        </Dialog>
      )}
    </div>
  )
}

function ThemeCard({
  item,
  balance,
  busy,
  onBuy,
  onEquip,
}: {
  item: ShopItem
  balance: number
  busy: boolean
  onBuy: () => void
  onEquip: () => void
}) {
  const locked = item.unlock_streak > 0 && !item.owned
  const shortfall = Math.max(0, item.price - balance)

  return (
    <div className={`shop-item ${item.active ? 'shop-item--active' : ''}`.trim()}>
      <div data-board-theme={item.code}>
        <Board
          className={`board--preview ${locked ? 'board--locked' : ''}`.trim()}
          givens={SAMPLE_GIVENS}
          board={SAMPLE_BOARD}
          notes={SAMPLE_NOTES}
        />
      </div>

      <div className="shop-item__body">
        <span className="shop-item__title">{item.title}</span>
        <span className="hint-text">{item.description}</span>

        {item.active && <span className="shop-item__state">{t('shop.chosen')}</span>}

        {!item.active && item.owned && (
          <button type="button" className="shop-item__button" onClick={onEquip} disabled={busy}>
            {item.unlock_streak > 0 ? t('shop.choose') : t('shop.bought')}
          </button>
        )}

        {locked && (
          <div className="stack stack--tight" style={{ marginTop: 2 }}>
            <span className="hint-text">{t('shop.locked', { days: item.unlock_streak })}</span>
            <ProgressBar
              value={item.unlock_progress}
              total={item.unlock_streak}
              variant="success"
            />
            <span className="hint-text" style={{ color: 'var(--success)', fontWeight: 600 }}>
              {t('shop.lockedProgress', {
                current: item.unlock_progress,
                total: item.unlock_streak,
              })}
            </span>
          </div>
        )}

        {!item.owned && !locked && (
          <>
            <button
              type="button"
              className="shop-item__button"
              onClick={onBuy}
              disabled={busy || shortfall > 0}
            >
              <Crystal>{item.price}</Crystal>
            </button>
            {shortfall > 0 && (
              <span className="error-text">{t('shop.notEnough', { amount: shortfall })}</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function FrameRow({
  item,
  balance,
  busy,
  onBuy,
  onEquip,
}: {
  item: ShopItem
  balance: number
  busy: boolean
  onBuy: () => void
  onEquip: () => void
}) {
  const shortfall = Math.max(0, item.price - balance)

  return (
    <div className={`shop-item ${item.active ? 'shop-item--active' : ''}`.trim()}>
      <div className="shop-item__body">
        <span className="shop-item__title">{item.title}</span>
        <span className="hint-text">{item.description}</span>
        {item.active ? (
          <span className="shop-item__state">{t('shop.chosen')}</span>
        ) : item.owned ? (
          <button type="button" className="shop-item__button" onClick={onEquip} disabled={busy}>
            {t('shop.bought')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="shop-item__button"
              onClick={onBuy}
              disabled={busy || shortfall > 0}
            >
              <Crystal>{item.price}</Crystal>
            </button>
            {shortfall > 0 && (
              <span className="error-text">{t('shop.notEnough', { amount: shortfall })}</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
